import { useEffect } from "react";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { SuccessTypography } from "components/SuccessTypography";
import { useWriterComponentState } from "hooks";

import { ClientAppEvent } from "../../../constants";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  shipmentType: ShipmentType;
  onRefresh: () => void;
  trackingLinkId: number;
}

export function DeleteShipmentTrackingLinkModal(props: Props) {
  const { isOpen, onClose, shipmentType, onRefresh, trackingLinkId } = props;
  const posthog = usePostHog();
  const { loading, error, write, success, resetState } =
    useWriterComponentState({
      errorMessage:
        "Failed to delete tracking link. Please contact us if the issue persists.",
      writeConfig: {
        method: "delete",
        url: `/tracking-links/${trackingLinkId}`,
      },
      errorMessageKey: "message",
    });

  useEffect(() => {
    if (success && isOpen) {
      posthog?.capture(ClientAppEvent.TRACKING_LINK_DELETED, {
        shipment_type: shipmentType,
      });
      onRefresh();
      onClose();
      resetState();
    }
  }, [
    success,
    onRefresh,
    onClose,
    isOpen,
    resetState,
    posthog,
    trackingLinkId,
    shipmentType,
  ]);

  return (
    <Dialog fullWidth open={isOpen} onClose={onClose}>
      <DialogTitle component="div">
        <Typography variant="h4">Delete Tracking Link</Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Are you sure want to delete this link?
        </Typography>
        <SuccessTypography success={success} message="Tracking link deleted." />
        <ErrorTypography error={error} />
      </DialogContent>
      {!loading && (
        <DialogActions>
          {loading ? (
            <Stack>
              <CircularProgress />
            </Stack>
          ) : (
            <>
              <Button variant="contained" color="error" onClick={() => write()}>
                Delete
              </Button>
              <Button variant="outlined" onClick={onClose}>
                Cancel
              </Button>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
